import React, { useEffect, useState } from "react";
import { GoogleMap, Marker, InfoWindow, useLoadScript } from "@react-google-maps/api";
import styles from "./GoogleMaps.module.css";
import { datadogLogs } from "@datadog/browser-logs";

type MapProps = {
  data: {
    maplatitude?: string | number;
    maplongitude?: string | number;
  };
  style?: React.CSSProperties;
};

const libraries: ("places" | "geocoding")[] = ["places", "geocoding"]; // Keep this constant to avoid reloading warnings

const GoogleMaps = ({ data, style }: MapProps) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_MAP_KEY || "",
    libraries,
  });

  const [center, setCenter] = useState({ lat: 0, lng: 0 });
  const [zoomMap, setZoomMap] = useState(15);
  const [infoWindowVisible, setInfoWindowVisible] = useState(false);
  const [address, setAddress] = useState("");

  useEffect(() => {
    if (!isLoaded || !data?.maplatitude || !data?.maplongitude) return;

    const latitude = parseFloat(String(data.maplatitude)) || 0;
    const longitude = parseFloat(String(data.maplongitude)) || 0;

    if (!isNaN(latitude) && !isNaN(longitude)) {
      setCenter({ lat: latitude, lng: longitude });

      // Ensure Geocoder is only called when Google Maps is loaded
      if (window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
          if (status === "OK" && results[0]) {
            setAddress(results[0].formatted_address || "Address not found");
          } else {
            datadogLogs.logger.info("Geocode was not successful:",{status});
          }
        });
      } else {
        datadogLogs.logger.info("Geocoder is not available in the google.maps object.");
      }
    }
  }, [isLoaded, data]);

  const mapStyles = {
    height: "420px",
    width: "1292px",
    borderRadius: "24px",
  };

  const handleInfoWindowClick = () => {
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${center.lat},${center.lng}`;
    window.open(googleMapsUrl, "_blank");
  };

  if (loadError) return <p>Error loading Google Maps</p>;
  if (!isLoaded) return <p>Loading Google Maps...</p>;

  return (
    <div className={`container g-map-sec ${styles.map_wrapper}`} data-testid="gmaps-container-id">
      <div className="row">
        <GoogleMap center={center} zoom={zoomMap} mapContainerStyle={style || mapStyles}>
          <Marker position={center} onClick={() => setInfoWindowVisible(true)} />
          {infoWindowVisible && (
            <InfoWindow position={center} onCloseClick={() => setInfoWindowVisible(false)}>
              <div className={styles.infoWindowContent} onClick={handleInfoWindowClick}>
                {address || "No address available"}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>
    </div>
  );
};

export default GoogleMaps;
